/**
 * PersistentPlayer — sticky mini-player bar that lives above the mobile
 * bottom nav and as a floating card on desktop. Appears whenever a track
 * is loaded via AudioContext; stays alive across all page navigations.
 */
import { useAudio } from '../contexts/AudioContext';
import { useNavigate } from 'react-router-dom';
import {
  Play, Pause, X, Radio, Mic2, ChevronUp,
  SkipBack, SkipForward
} from 'lucide-react';

function fmtTime(s) {
  if (!isFinite(s) || s <= 0) return '--:--';
  const m   = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export default function PersistentPlayer() {
  const { currentTrack, isPlaying, isLoading, currentTime, duration,
          togglePlay, seek, stop } = useAudio();
  const navigate = useNavigate();

  if (!currentTrack) return null;

  const isRadio   = currentTrack.type === 'radio';
  const progress  = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleSeekClick = (e) => {
    const bar  = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const pct  = (e.clientX - rect.left) / rect.width;
    seek(pct * duration);
  };

  const skipBack    = () => seek(Math.max(0, currentTime - 15));
  const skipForward = () => seek(currentTime + 30);

  const Spinner = () => (
    <div className="w-4 h-4 border-2 border-mint-fresh border-t-transparent rounded-full animate-spin" />
  );

  const PlayBtn = ({ size = 'sm' }) => (
    <button
      onClick={togglePlay}
      className={`rounded-full bg-dark-slate flex items-center justify-center
        active:scale-95 transition-transform flex-shrink-0
        ${size === 'sm' ? 'w-9 h-9' : 'w-10 h-10'}`}
      aria-label={isPlaying ? 'Pause' : 'Play'}
    >
      {isLoading ? <Spinner />
        : isPlaying
          ? <Pause className={`${size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} text-mint-fresh`} />
          : <Play  className={`${size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} text-mint-fresh ml-0.5`} />}
    </button>
  );

  const Artwork = ({ cls }) => currentTrack.imageUrl
    ? <img src={currentTrack.imageUrl} alt="" className={`${cls} object-cover flex-shrink-0`} />
    : (
      <div className={`${cls} bg-dark-slate flex items-center justify-center flex-shrink-0`}>
        {isRadio
          ? <Radio className="w-5 h-5 text-mint-fresh" />
          : <Mic2  className="w-5 h-5 text-mint-fresh" />}
      </div>
    );

  const ProgressBar = ({ className = '' }) =>
    !isRadio && (
      <div
        className={`h-1 w-full cursor-pointer ${className}`}
        style={{ backgroundColor: 'hsl(var(--border))' }}
        onClick={handleSeekClick}
        role="slider"
        aria-label="Seek"
      >
        <div
          className="h-full bg-mint-fresh transition-[width] duration-500 ease-linear pointer-events-none"
          style={{ width: `${progress}%` }}
        />
      </div>
    );

  const LiveBadge = () => isRadio && (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-500 uppercase tracking-wider">
      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
      Live
    </span>
  );

  return (
    <>
      {/* ── Mobile: bar above bottom nav ── */}
      <div
        className="md:hidden fixed left-0 right-0 z-40 shadow-lg"
        style={{
          bottom: '64px',
          backgroundColor: 'hsl(var(--card))',
          borderTop: '1px solid hsl(var(--border))',
        }}
      >
        <ProgressBar />

        <div className="flex items-center gap-2.5 px-3 py-2">
          <Artwork cls="w-10 h-10 rounded-lg" />

          <button
            className="flex-1 min-w-0 text-left"
            onClick={() => navigate('/hbcu-media')}
          >
            <p className="text-sm font-semibold text-dark-slate truncate leading-tight">
              {currentTrack.title}
            </p>
            <div className="flex items-center gap-1.5">
              <LiveBadge />
              {!isRadio && (
                <p className="text-xs text-slate-gray truncate">
                  {fmtTime(currentTime)}{duration > 0 && ` / ${fmtTime(duration)}`}
                </p>
              )}
              {currentTrack.artist && (
                <p className="text-xs text-slate-gray truncate">· {currentTrack.artist}</p>
              )}
            </div>
          </button>

          {/* Podcast skip controls */}
          {!isRadio && (
            <>
              <button onClick={skipBack} className="w-7 h-7 flex items-center justify-center text-slate-gray" aria-label="Back 15s">
                <SkipBack className="w-3.5 h-3.5" />
              </button>
            </>
          )}

          <PlayBtn size="sm" />

          {!isRadio && (
            <button onClick={skipForward} className="w-7 h-7 flex items-center justify-center text-slate-gray" aria-label="Forward 30s">
              <SkipForward className="w-3.5 h-3.5" />
            </button>
          )}

          <button
            onClick={stop}
            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center flex-shrink-0"
            aria-label="Stop"
          >
            <X className="w-4 h-4 text-slate-gray" />
          </button>
        </div>
      </div>

      {/* ── Desktop: floating card, bottom-right ── */}
      <div
        className="hidden md:block fixed bottom-6 right-6 z-40 shadow-2xl rounded-2xl overflow-hidden w-80"
        style={{
          backgroundColor: 'hsl(var(--card))',
          border: '1px solid hsl(var(--border))',
        }}
      >
        <ProgressBar />

        <div className="flex items-center gap-3 px-4 py-3">
          <Artwork cls="w-12 h-12 rounded-xl" />

          <button
            className="flex-1 min-w-0 text-left"
            onClick={() => navigate('/hbcu-media')}
          >
            <p className="text-sm font-semibold text-dark-slate truncate">{currentTrack.title}</p>
            <div className="flex items-center gap-1.5">
              <LiveBadge />
              {!isRadio && (
                <p className="text-xs text-slate-gray">
                  {fmtTime(currentTime)}{duration > 0 && ` / ${fmtTime(duration)}`}
                </p>
              )}
              {currentTrack.artist && (
                <p className="text-xs text-slate-gray truncate">· {currentTrack.artist}</p>
              )}
            </div>
          </button>

          {!isRadio && (
            <button onClick={skipBack} className="w-8 h-8 flex items-center justify-center text-slate-gray hover:text-dark-slate" aria-label="Back 15s">
              <SkipBack className="w-4 h-4" />
            </button>
          )}

          <PlayBtn size="md" />

          {!isRadio && (
            <button onClick={skipForward} className="w-8 h-8 flex items-center justify-center text-slate-gray hover:text-dark-slate" aria-label="Forward 30s">
              <SkipForward className="w-4 h-4" />
            </button>
          )}

          <button
            onClick={stop}
            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center flex-shrink-0"
            aria-label="Stop"
          >
            <X className="w-4 h-4 text-slate-gray" />
          </button>
        </div>
      </div>
    </>
  );
}
